/**
 * De regels van de wachtrij, los van de opslag (Stap 12).
 *
 * Wat een medewerker offline doet, moet later aankomen: hij bevestigt een instructie in een koelcel,
 * loopt om 14:00 naar de kantine en dan hoort het er te staan — met het tijdstip van 06:55 en niet
 * dat van 14:00 (ADR-0047).
 *
 * Deze beslissingen staan apart van IndexedDB zodat ze te testen zijn zonder browser. Elke regel
 * hieronder bestaat omdat het alternatief een zichtbare fout op de vloer oplevert.
 */

/** Hoe lang de wachtrij het blijft proberen. Daarna neemt de server het tijdstip niet meer aan. */
export const MAX_QUEUE_AGE_DAYS = 7;

/** Na hoeveel mislukte pogingen we een regel laten liggen tot de volgende gelegenheid. */
export const MAX_ATTEMPTS_PER_ROUND = 3;

/**
 * De soorten regels in de wachtrij.
 *
 * `checklist-item` kwam er bij in Stap 38. Dat is de reden dat `postId` hieronder niet meer altijd
 * een bericht is — zie `OutboxEntry`.
 */
export type OutboxKind = 'seen' | 'acknowledge' | 'checklist-item';

export interface OutboxEntry {
  /** De idempotency-sleutel; ook de sleutel in de opslag. */
  id: string;
  kind: OutboxKind;
  /**
   * Waar de regel over gaat: een bericht bij `seen`/`acknowledge`, een **uitvoering** bij
   * `checklist-item`.
   *
   * De naam is van Stap 12 en dekt sinds Stap 38 niet alles meer. Hij blijft staan omdat
   * omdopen betekent dat elke regel die nu in de IndexedDB van een telefoon staat, bij de
   * volgende versie niet meer gelezen wordt — en dat zijn precies de bevestigingen die niet
   * mogen verdwijnen. Genoteerd in `docs/product/demo-schuld.md`.
   */
  postId: string;
  /** Wanneer de medewerker het deed, volgens zijn toestel. */
  clientOccurredAt: string;
  /** Alleen bij een bevestiging: de versie en de taal die op zijn scherm stonden. */
  postVersion?: number;
  languageShown?: string;
  /** Alleen bij `checklist-item`: welk item, en wat er ingevuld werd. */
  itemId?: string;
  value?: string;
  comment?: string;
  attempts: number;
  lastError?: string;
}

/**
 * De sleutel van een regel.
 *
 * `kind` plus `postId` plus het tijdstip, en dus **niet** een willekeurig id: twee keer op dezelfde
 * knop drukken terwijl je offline bent, hoort één regel te zijn. Zonder dit zou een medewerker die
 * twijfelt of zijn tik aankwam (en dat doet hij, want er is geen netwerk) drie bevestigingen in de
 * wachtrij zetten.
 *
 * Het tijdstip zit erin en niet alleen `kind:postId`, omdat "gezien" van vandaag en van morgen twee
 * gebeurtenissen zijn. Bij een bevestiging maakt het niets uit: de server houdt er toch maar één.
 */
export function outboxKeyOf(input: {
  kind: OutboxKind;
  postId: string;
  clientOccurredAt: string;
  itemId?: string;
}): string {
  // Bij een checklist-item hoort het item in de sleutel, en het tijdstip **niet**: twee keer
  // hetzelfde item afvinken is één handeling met één uitkomst, en de laatste waarde is de juiste.
  // Het tijdstip van de eerste poging zou anders een tweede regel in de wachtrij maken, en dan
  // overschrijft de oudste op de server de nieuwste.
  if (input.kind === 'checklist-item') {
    return `checklist-item:${input.postId}:${input.itemId ?? ''}`;
  }
  return `${input.kind}:${input.postId}:${input.clientOccurredAt}`;
}

/**
 * Is deze regel te oud om nog te versturen?
 *
 * De server weigert een tijdstip ouder dan zeven dagen met een `422`. Zelf opruimen is netter dan
 * wachten tot de server het zegt: anders staat er een regel in de wachtrij die bij elke
 * netwerkverbinding opnieuw een mislukt verzoek doet, en die badge "wacht op verzending" blijft dan
 * eeuwig staan bij een bevestiging die nooit meer aankomt.
 */
export function isExpired(entry: OutboxEntry, now = new Date()): boolean {
  const age = now.getTime() - new Date(entry.clientOccurredAt).getTime();
  return !Number.isFinite(age) || age > MAX_QUEUE_AGE_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * De orde waarin de wachtrij verstuurd wordt: oudste eerst, en "gezien" vóór "bevestigd".
 *
 * Bij hetzelfde tijdstip gaat `seen` voor. Dat is geen schoonheid maar een gevolg: de server maakt
 * bij een bevestiging zelf ook een gezien-rij aan, en als die al bestaat met het juiste tijdstip,
 * blijft het eerste tijdstip staan (ADR-0040). Omgekeerd zou "gezien" het tijdstip van de
 * bevestiging krijgen, en dat is een seconde later dan de waarheid.
 */
export function inSendOrder(entries: OutboxEntry[]): OutboxEntry[] {
  const rank = (kind: OutboxKind) => (kind === 'seen' ? 0 : 1);

  return [...entries].sort((left, right) => {
    const byTime = left.clientOccurredAt.localeCompare(right.clientOccurredAt);
    return byTime !== 0 ? byTime : rank(left.kind) - rank(right.kind);
  });
}

/**
 * Wat er met een mislukte poging moet gebeuren.
 *
 * Het onderscheid dat hier gemaakt wordt, is hetzelfde als bij het pushkanaal (Stap 9): een fout van
 * onze kant is iets anders dan een netwerk dat er even niet is.
 *
 * - **`422` en `409`**: de server heeft geoordeeld en dat oordeel verandert niet. Weggooien, en bij
 *   een `409` hoort de medewerker te zien dat het bericht gewijzigd is.
 * - **`401`**: de sessie is weg. Laten staan — na opnieuw aanmelden komt hij alsnog aan. (Is de
 *   sessie werkelijk ingetrokken, dan wordt de hele opslag gewist, wachtrij inbegrepen.)
 * - **Netwerkfout of `5xx`**: later opnieuw.
 */
export type RetryDecision = 'weggooien' | 'later' | 'conflict';

export function decideRetry(status: number | null): RetryDecision {
  if (status === null) return 'later';
  if (status === 409) return 'conflict';
  if (status === 422 || status === 400 || status === 404 || status === 403) return 'weggooien';
  return 'later';
}

/**
 * Welke schermen opnieuw moeten ophalen nadat een soort regel aankwam (hertest 29 okt.).
 *
 * Wat offline gebeurde, staat na verzenden op de server, maar de schermen die erover gaan hebben
 * nog hun antwoord van vóór de verzending. Werd niets ververst, dan toonde een offline afgevinkt
 * item na "weer online" opnieuw de oude stand ("1 van 2") tot de medewerker herlaadde.
 *
 * Per soort en niet per scherm: wie een nieuwe soort regel toevoegt, zet hier wat erbij hoort.
 * Het zijn voorvoegsels van querysleutels; `['me', 'checklists']` ververst ook de rondedetail.
 */
export const QUERIES_AFTER_SENDING: Record<OutboxKind, readonly (readonly string[])[]> = {
  seen: [['feed']],
  acknowledge: [['feed'], ['my-reach'], ['open-items']],
  'checklist-item': [['me', 'checklists'], ['open-items']],
};

/** De querysleutels om te verversen na het verzenden van deze soorten, zonder dubbels. */
export function queriesAfterSending(kinds: Iterable<OutboxKind>): (readonly string[])[] {
  const seen = new Map<string, readonly string[]>();
  for (const kind of kinds) {
    for (const key of QUERIES_AFTER_SENDING[kind]) seen.set(JSON.stringify(key), key);
  }
  return [...seen.values()];
}

/**
 * Hoort een checklist-item nog als "afgevinkt op dit toestel" op het scherm (hertest 29 okt.)?
 *
 * Zolang het in de wachtrij staat: ja. Ook nadat de server het aannam, **tot de gegevens van het
 * scherm opgehaald zijn ná dat moment**. Anders verdwijnt de regel uit de wachtrij terwijl het
 * scherm nog de stand van vóór de verzending toont (of een ophaling die begon toen het netwerk
 * terugkwam, nét vóór de verzending), en springt het item even terug naar open.
 *
 * `sentAt` is het moment waarop de server de regel aannam, `null` als hij nog wacht;
 * `fetchStartedAt` het moment waarop de getoonde gegevens werden opgevraagd.
 */
export function stillShownAsLocal(sentAt: number | null, fetchStartedAt: number): boolean {
  return sentAt === null || sentAt > fetchStartedAt;
}
